<template>
  <div class="flex min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
    
    <!-- 1. Dynamic Sidebar -->
    <component 
      :is="layoutMap[currentModule].sidebar" 
      v-model:isOpen="isSidebarOpen" 
    />

    <!-- 2. Main Content Wrapper -->
    <!-- md:ml-64 pushes this entire section to the right of the sidebar on desktop -->
    <div class="flex flex-1 flex-col min-w-0 transition-all duration-300 md:ml-64">
      
      <!-- 3. Topbar (Sticky) -->
      <header class="sticky top-0 z-30 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-700">
        <component 
          :is="layoutMap[currentModule].topbar"
          :page-title="currentPageTitle"
          :is-sidebar-open="isSidebarOpen"
          :is-dark-mode="isDarkMode"
          @toggle-sidebar="toggleSidebar"
          @toggle-dark-mode="toggleDarkMode"
        />
      </header>

      <!-- 4. Scrollable Page Content -->
      <main class="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
        
        <!-- Loading State -->
        <div v-if="loading" class="flex flex-col justify-center items-center h-64">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-[#035a76]"></div>
          <p class="mt-4 text-gray-600 dark:text-gray-400 font-medium">Loading profile...</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 p-6 rounded-xl flex items-start gap-3">
          <svg class="h-6 w-6 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p class="font-semibold">Failed to load employee profile</p>
            <p class="mt-1 text-sm">{{ error }}</p>
          </div>
        </div>

        <!-- Content -->
        <template v-else-if="employee && employee.personal">
          
          <!-- Profile Header Card -->
          <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 mb-8">
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
              <div class="flex items-center gap-5">
                <!-- Avatar / Initials -->
                <div class="h-16 w-16 sm:h-20 sm:w-20 rounded-full bg-gradient-to-br from-[#035a76] to-[#04698f] flex items-center justify-center text-white text-2xl sm:text-3xl font-bold shadow-md flex-shrink-0">
                  {{ getInitials(employee.personal.first_name, employee.personal.last_name) }}
                </div>
                <div>
                  <h1 class="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                    {{ employee.personal.first_name }} {{ employee.personal.last_name }}
                  </h1>
                  <div class="flex flex-wrap items-center gap-3 mt-2">
                    <span class="text-sm text-gray-500 dark:text-gray-400">
                      ID: <span class="font-mono font-medium text-gray-700 dark:text-gray-300">{{ employee.personal.employee_number || 'N/A' }}</span>
                    </span>
                    <span class="hidden sm:inline text-gray-300 dark:text-gray-600">•</span>
                    <span class="text-sm text-gray-500 dark:text-gray-400">
                      {{ employee.personal.position_title || 'Staff Member' }}
                    </span>
                  </div>
                </div>
              </div>

              <div class="flex items-center gap-3 sm:flex-col sm:items-end">
                <span :class="getStatusClass(employee.contract?.status)" class="px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide w-fit">
                  {{ employee.contract?.status || 'No Contract' }}
                </span>
              </div>
            </div>
          </div>

          <!-- Main Grid -->
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 xl:gap-8">
            
            <!-- Personal Information -->
            <section class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
              <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-5 pb-3 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
                <svg class="w-5 h-5 text-[#035a76]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                Personal Information
              </h2>
              <dl class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
                <div>
                  <dt class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Employee Number</dt>
                  <dd class="mt-1 text-sm font-medium text-gray-900 dark:text-white font-mono">{{ employee.personal.employee_number || '—' }}</dd>
                </div>
                <div>
                  <dt class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Full Name</dt>
                  <dd class="mt-1 text-sm font-medium text-gray-900 dark:text-white">
                    {{ [employee.personal.first_name, employee.personal.middle_name, employee.personal.last_name].filter(Boolean).join(' ') || '—' }}
                  </dd>
                </div>
                <div>
                  <dt class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Gender</dt>
                  <dd class="mt-1 text-sm font-medium text-gray-900 dark:text-white capitalize">{{ employee.personal.gender || '—' }}</dd>
                </div>
                <div>
                  <dt class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Date of Birth</dt>
                  <dd class="mt-1 text-sm font-medium text-gray-900 dark:text-white">{{ formatDate(employee.personal.date_of_birth) }}</dd>
                </div>
                <div>
                  <dt class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Place of Birth</dt>
                  <dd class="mt-1 text-sm font-medium text-gray-900 dark:text-white">{{ employee.personal.place_of_birth || '—' }}</dd>
                </div>
                <div>
                  <dt class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Nationality</dt>
                  <dd class="mt-1 text-sm font-medium text-gray-900 dark:text-white">{{ employee.personal.nationality || '—' }}</dd>
                </div>
                <div>
                  <dt class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Marital Status</dt>
                  <dd class="mt-1 text-sm font-medium text-gray-900 dark:text-white capitalize">{{ employee.personal.marital_status || '—' }}</dd>
                </div>
                <div>
                  <dt class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Date of Joining</dt>
                  <dd class="mt-1 text-sm font-medium text-gray-900 dark:text-white">{{ formatDate(employee.personal.date_of_join) }}</dd>
                </div>
              </dl>
            </section>

            <!-- Contact & Emergency -->
            <section class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
              <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-5 pb-3 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
                <svg class="w-5 h-5 text-[#035a76]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                Contact & Emergency
              </h2>
              <dl class="space-y-5">
                <div>
                  <dt class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Phone Number</dt>
                  <dd class="mt-1 text-sm font-medium text-gray-900 dark:text-white">{{ employee.personal.phone || employee.contact?.phone || '—' }}</dd>
                </div>
                <div>
                  <dt class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Email Address</dt>
                  <dd class="mt-1 text-sm font-medium text-gray-900 dark:text-white break-all">{{ employee.personal.email || employee.contact?.email || '—' }}</dd>
                </div>
                <div>
                  <dt class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Residential Address</dt>
                  <dd class="mt-1 text-sm font-medium text-gray-900 dark:text-white whitespace-pre-line">{{ employee.personal.address || employee.contact?.residential_address || '—' }}</dd>
                </div>

                <div v-if="employee.emergency_contact?.name" class="pt-5 border-t border-gray-100 dark:border-gray-700">
                  <dt class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Emergency Contact</dt>
                  <dd class="mt-2 bg-gray-50 dark:bg-gray-700/40 p-4 rounded-lg border border-gray-100 dark:border-gray-600">
                    <p class="font-semibold text-gray-900 dark:text-white">{{ employee.emergency_contact.name }}</p>
                    <p class="text-sm text-gray-600 dark:text-gray-300 mt-1">
                      {{ employee.emergency_contact.relationship }} • {{ employee.emergency_contact.phone }}
                      <span v-if="employee.emergency_contact.alt_phone" class="text-gray-400"> / {{ employee.emergency_contact.alt_phone }}</span>
                    </p>
                    <p v-if="employee.emergency_contact.address" class="text-xs text-gray-500 dark:text-gray-400 mt-2 whitespace-pre-line">
                      {{ employee.emergency_contact.address }}
                    </p>
                  </dd>
                </div>
              </dl>
            </section>

            <!-- Banking, Identification & Contract -->
            <section class="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
              <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-5 pb-3 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
                <svg class="w-5 h-5 text-[#035a76]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                Banking, Identification & Contract
              </h2>
              <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
                
                <!-- Bank Account -->
                <div>
                  <h3 class="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide mb-3">Bank Account</h3>
                  <dl class="space-y-3">
                    <div>
                      <dt class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Bank Name</dt>
                      <dd class="mt-1 text-sm font-medium text-gray-900 dark:text-white">{{ employee.bank_account?.bank_name || '—' }}</dd>
                    </div>
                    <div>
                      <dt class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Branch</dt>
                      <dd class="mt-1 text-sm font-medium text-gray-900 dark:text-white">{{ employee.bank_account?.branch_name || '—' }}</dd>
                    </div>                    
                    <div>
                      <dt class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Account Name</dt>
                      <dd class="mt-1 text-sm font-medium text-gray-900 dark:text-white">{{ employee.bank_account?.account_name || '—' }}</dd>
                    </div>
                    <div>
                      <dt class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Account Number</dt>
                      <dd class="mt-1 text-sm font-mono font-medium text-gray-900 dark:text-white">{{ employee.bank_account?.account_number || '—' }}</dd>
                    </div>
                  </dl>
                </div>

                <!-- Identification -->
                <div>
                  <h3 class="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide mb-3">Identification</h3>
                  <dl class="space-y-3">
                    <div>
                      <dt class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Ghana Card</dt>
                      <dd class="mt-1 text-sm font-medium text-gray-900 dark:text-white">{{ employee.identifications?.ghana_card_number || '—' }}</dd>
                    </div>
                    <div>
                      <dt class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Expiry Date</dt>
                      <dd class="mt-1 text-sm font-medium text-gray-900 dark:text-white">{{ formatDate(employee.identifications?.ghana_card_expiry) }}</dd>
                    </div>
                    <div>
                      <dt class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">SSNIT Number</dt>
                      <dd class="mt-1 text-sm font-medium text-gray-900 dark:text-white">{{ employee.identifications?.ssnit_number || '—' }}</dd>
                    </div>
                  </dl>
                </div>

                <!-- Current Contract -->
                <div v-if="employee.contract">
                  <h3 class="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide mb-3">Current Contract</h3>
                  <dl class="space-y-3">
                    <div>
                      <dt class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Employee Code</dt>
                      <dd class="mt-1 text-sm font-medium text-gray-900 dark:text-white font-mono">{{ employee.contract?.employee_code || '—' }}</dd>
                    </div>
                    <div>
                      <dt class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Status</dt>
                      <dd class="mt-1">
                        <span :class="getStatusClass(employee.contract?.status)" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium">
                          {{ employee.contract?.status || 'No contract' }}
                        </span>
                      </dd>
                    </div>
                  </dl>
                </div>
                <div v-else class="md:col-span-1 flex items-center justify-center p-6 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-dashed border-gray-300 dark:border-gray-600">
                  <p class="text-sm text-gray-500 dark:text-gray-400 text-center">No active contract found</p>
                </div>
              </div>
            </section>

            <!-- Family Information -->
            <section class="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
              <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-5 pb-3 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
                <svg class="w-5 h-5 text-[#035a76]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                Family Information
              </h2>

              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <!-- Spouse -->
                <div v-if="employee.spouse">
                  <h3 class="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide mb-3">Spouse</h3>
                  <div class="bg-gray-50 dark:bg-gray-700/40 p-4 rounded-lg border border-gray-100 dark:border-gray-600">
                    <p class="font-semibold text-gray-900 dark:text-white">{{ employee.spouse.name || '—' }}</p>
                    <p class="text-sm text-gray-600 dark:text-gray-300 mt-1">{{ employee.spouse.phone || '—' }}</p>
                  </div>
                </div>

                <!-- Children -->
                <div v-if="employee.children?.length">
                  <h3 class="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide mb-3">Children ({{ employee.children.length }})</h3>
                  <div class="space-y-3">
                    <div v-for="(child, i) in employee.children" :key="child.id || i" class="bg-gray-50 dark:bg-gray-700/40 p-4 rounded-lg border border-gray-100 dark:border-gray-600">
                      <p class="font-semibold text-gray-900 dark:text-white">{{ child.full_name || '—' }}</p>
                      <p class="text-sm text-gray-600 dark:text-gray-300 mt-1">Age: {{ child.age || '—' }}</p>
                    </div>
                  </div>
                </div>

                <!-- Parents -->
                <div v-if="employee.parents?.length">
                  <h3 class="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide mb-3">Parents</h3>
                  <div class="space-y-3">
                    <div v-for="parent in employee.parents" :key="parent.id" class="bg-gray-50 dark:bg-gray-700/40 p-4 rounded-lg border border-gray-100 dark:border-gray-600">
                      <p class="font-semibold text-gray-900 dark:text-white capitalize">{{ parent.parent_type }} • {{ parent.name }}</p>
                      <p class="text-sm text-gray-600 dark:text-gray-300 mt-1">{{ parent.contact || '—' }}</p>
                      <p v-if="parent.address" class="text-xs text-gray-500 dark:text-gray-400 mt-2 whitespace-pre-line">{{ parent.address }}</p>
                    </div>
                  </div>
                </div>

                <!-- Empty State for Family -->
                <div v-if="!employee.spouse && !employee.children?.length && !employee.parents?.length" class="md:col-span-2 lg:col-span-3 flex items-center justify-center p-8 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-dashed border-gray-300 dark:border-gray-600">
                  <p class="text-sm text-gray-500 dark:text-gray-400">No family information recorded</p>
                </div>
              </div>
            </section>
          </div>

          <!-- Education & Work Experience -->
          <div class="mt-8 space-y-8">
            
            <!-- Education -->
            <section v-if="employee.education?.length" class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
              <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-5 pb-3 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
                <svg class="w-5 h-5 text-[#035a76]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M12 14l9-5-9-5-9 5 9 5z" /><path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" /><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" /></svg>
                Education Background
              </h2>
              <div class="space-y-6">
                <div v-for="edu in employee.education" :key="edu.id" class="relative pl-6 border-l-2 border-[#035a76] dark:border-blue-500">
                  <div class="absolute -left-[9px] top-1.5 h-4 w-4 rounded-full bg-[#035a76] dark:bg-blue-500 border-2 border-white dark:border-gray-800"></div>
                  <p class="font-semibold text-gray-900 dark:text-white text-base">
                    {{ edu.certificate || 'Certificate' }}
                    <span v-if="edu.other_certificate" class="text-sm font-normal text-gray-500 dark:text-gray-400">({{ edu.other_certificate }})</span>
                  </p>
                  <p class="text-gray-700 dark:text-gray-300 mt-1">{{ edu.institution || '—' }}</p>
                  <p class="text-sm text-gray-500 dark:text-gray-400 mt-1 font-medium">
                    {{ edu.start_year || '—' }} — {{ edu.end_year || 'Present' }}
                  </p>
                </div>
              </div>
            </section>

            <!-- Work Experience -->
            <section v-if="employee.work_experience?.length" class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
              <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-5 pb-3 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
                <svg class="w-5 h-5 text-[#035a76]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                Professional Experience
              </h2>
              <div class="space-y-6">
                <div v-for="work in employee.work_experience" :key="work.id" class="relative pl-6 border-l-2 border-[#76ba51] dark:border-green-500">
                  <div class="absolute -left-[9px] top-1.5 h-4 w-4 rounded-full bg-[#76ba51] dark:bg-green-500 border-2 border-white dark:border-gray-800"></div>
                  <p class="font-semibold text-gray-900 dark:text-white text-base">
                    {{ work.position }} <span class="text-gray-500 dark:text-gray-400 font-normal">at</span> {{ work.company_name || '—' }}
                  </p>
                  <p class="text-sm text-gray-600 dark:text-gray-300 mt-1">{{ work.company_address || '—' }}</p>
                  <p class="text-sm text-gray-500 dark:text-gray-400 mt-2 font-medium">
                    {{ formatDate(work.start_date) }} — {{ formatDate(work.end_date) || 'Present' }}
                  </p>
                  <p v-if="work.reason_for_leaving" class="mt-2 text-sm text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/40 p-3 rounded-lg">
                    <span class="font-semibold text-gray-700 dark:text-gray-200">Reason for leaving:</span> {{ work.reason_for_leaving }}
                  </p>
                  <p v-if="work.contact_person_phone" class="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    <span class="font-semibold">Reference Contact:</span> {{ work.contact_person_phone }}
                  </p>
                </div>
              </div>
            </section>
          </div>

        </template>

        <!-- No data state -->
        <div v-else class="flex flex-col items-center justify-center py-20 text-gray-500 dark:text-gray-400">
          <svg class="h-16 w-16 mb-4 text-gray-300 dark:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p class="text-lg font-medium">No employee data available</p>
        </div>

      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import api from '@/services/api'
import { useRoute, useRouter } from 'vue-router'

import HrSidebar from '@/components/hr/HrSidebar.vue'
import AcdSidebar from '@/components/acd/AcdSidebar.vue'
import CommSidebar from '@/components/com/ComSidebar.vue'
import HrTopbar from '@/components/hr/HrTopbar.vue'
import AcdTopbar from '@/components/acd/AcdTopbar.vue'
import CommTopbar from '@/components/com/ComTopbar.vue'
import ItSidebar from '@/components/it/ItSidebar.vue'
import ItTopbar from '@/components/it/ItTopbar.vue'

const layoutMap = {
  hr: { sidebar: HrSidebar, topbar: HrTopbar },
  academics: { sidebar: AcdSidebar, topbar: AcdTopbar },
  it: { sidebar: ItSidebar, topbar: ItTopbar },
  com: { sidebar: CommSidebar, topbar: CommTopbar },
}

const route = useRoute()
const router = useRouter()

const currentModule = computed(() => {
  if (route.path.startsWith('/academics')) return 'academics'
  if (route.path.startsWith('/it')) return 'it'
  if (route.path.startsWith('/com')) return 'com'
  if (route.path.startsWith('/procurement')) return 'procurement'
  return 'hr'
})

const applicantId = route.params.id

const isSidebarOpen = ref(false) // Hidden by default on mobile
const isDarkMode = ref(false)
const currentPageTitle = ref('Staff Details')

const employee = ref({})
const loading = ref(true)
const error = ref(null)

const toggleSidebar = () => { isSidebarOpen.value = !isSidebarOpen.value }

const toggleDarkMode = () => {
  isDarkMode.value = !isDarkMode.value
  if (isDarkMode.value) {
    document.documentElement.classList.add('dark')
    localStorage.theme = 'dark'
  } else {
    document.documentElement.classList.remove('dark')
    localStorage.theme = 'light'
  }
}

const getInitials = (first, last) => {
  const f = first ? first.charAt(0).toUpperCase() : ''
  const l = last ? last.charAt(0).toUpperCase() : ''
  return (f + l) || '??'
}

const formatDate = (dateString) => {
  if (!dateString) return '—'
  try {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  } catch {
    return '—'
  }
}

const getStatusClass = (status) => {
  const s = (status || '').toLowerCase()
  if (s === 'active' || s === 'hired') return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
  if (s === 'on leave' || s === 'pending') return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300'
  if (s === 'inactive' || s === 'terminated') return 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
  return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
}

const fetchEmployeeProfile = async () => {
  try {
    loading.value = true
    error.value = null
    const response = await api.get(`get_personal_info.php`)
    if (response.data?.success) {
      employee.value = response.data
    } else {
      error.value = 'Invalid response from server'
    }
  } catch (err) {
    console.error('Error fetching employee profile:', err)
    error.value = err.message || 'Network error'
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchEmployeeProfile()
})
</script>